// src/components/sidebar/SidebarResponsive.js
// ENHANCED: Modern slide-in animations, glass morphism, improved mobile UX
// Features: Smooth drawer transitions, backdrop blur, enhanced visual feedback
//
// --- UPDATE: the drawer closes after a link is tapped (Content onNavigate); useColorModeValue
//   calls hoisted out of JSX; unused imports removed.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { getUserInfo } from "../../utils/auth"; // --- UPDATE

// chakra imports
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  Icon,
  useColorModeValue,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import Content from "./components/Content";
import {
  renderThumb,
  renderTrack,
  renderView,
} from "../scrollbar/Scrollbar";
import { Scrollbars } from "react-custom-scrollbars-2";

// Assets
import { IoMenuOutline } from "react-icons/io5";

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const menuButtonVariants = {
  idle: { scale: 1, rotate: 0 },
  hover: { 
    scale: 1.1, 
    rotate: 180,
    transition: { 
      type: "spring", 
      stiffness: 300,
      damping: 15
    }
  },
  tap: { scale: 0.95 }
};

export function SidebarResponsive(props) {
  // ============================================================================
  // ENHANCED THEME COLORS
  // ============================================================================
  let sidebarBackgroundColor = useColorModeValue(
    "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
    "linear-gradient(135deg, rgba(26,32,44,0.98) 0%, rgba(45,55,72,0.95) 100%)"
  );
  
  let menuColor = useColorModeValue("gray.700", "white");
  let menuBg = useColorModeValue("white", "navy.800");
  let menuHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  
  let overlayBg = useColorModeValue(
    "rgba(0, 0, 0, 0.3)",
    "rgba(0, 0, 0, 0.5)"
  );

  let borderColor = useColorModeValue(
    "rgba(226, 232, 240, 0.8)",
    "rgba(74, 85, 104, 0.4)"
  );

  let gradientAccent = useColorModeValue(
    "linear(to-r, blue.400, purple.500, teal.400)",
    "linear(to-r, blue.500, purple.600, teal.500)"
  );

  // --- UPDATE: hoisted (were inline in JSX)
  const tooltipBg = useColorModeValue("gray.700", "gray.300");
  const tooltipColor = useColorModeValue("white", "gray.800");
  const buttonShadow = useColorModeValue("0 2px 8px rgba(0,0,0,0.08)", "0 2px 8px rgba(0,0,0,0.3)");
  const drawerShadow = useColorModeValue("0 8px 32px 0 rgba(31, 38, 135, 0.2)", "0 8px 32px 0 rgba(0, 0, 0, 0.5)");
  const orb1 = useColorModeValue("radial(circle, blue.100, transparent)", "radial(circle, blue.900, transparent)");
  const orb2 = useColorModeValue("radial(circle, purple.100, transparent)", "radial(circle, purple.900, transparent)");
  // --- END UPDATE

  // Destructure props
  const {
    routes,
    isOpen,
    onOpen,
    onClose,
    scrolled,
    fixed,
    secondary,
    variant,
    logoText,
    message,
    brandText,
    ...rest
  } = props;
  
  const btnRef = React.useRef();
  const user = useMemo(() => { try { return getUserInfo() || undefined; } catch (_) { return undefined; } }, []); // --- UPDATE

  return (
    <Flex display={{ sm: "flex", xl: "none" }} alignItems="center" {...rest}>
      {/* Enhanced Hamburger Icon Button */}
      <Tooltip 
        label="Open Menu" 
        hasArrow 
        placement="bottom"
        bg={tooltipBg}
        color={tooltipColor}
      >
        <MotionBox
          ref={btnRef}
          variants={menuButtonVariants}
          initial="idle"
          whileHover="hover"
          whileTap="tap"
        >
          <IconButton
            icon={<Icon as={IoMenuOutline} />}
            onClick={onOpen}
            variant="ghost"
            color={menuColor}
            bg={menuBg}
            _hover={{
              bg: menuHoverBg,
              transform: "scale(1.05)",
            }}
            _active={{
              transform: "scale(0.95)",
            }}
            borderRadius="xl"
            boxSize="40px"
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            boxShadow={buttonShadow}
            aria-label="Open navigation menu"
          />
        </MotionBox>
      </Tooltip>

      {/* Enhanced Drawer with animations */}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === "rtl" ? "right" : "left"}
        finalFocusRef={btnRef}
        motionPreset="slideInLeft"
      >
        {/* Enhanced overlay with blur */}
        <DrawerOverlay 
          bg={overlayBg}
          backdropFilter="blur(4px)"
          transition="all 0.3s ease"
        />
        
        <DrawerContent 
          w="285px" 
          maxW="285px" 
          bgGradient={sidebarBackgroundColor}
          backdropFilter="blur(10px) saturate(180%)"
          boxShadow={drawerShadow}
          borderRightWidth="1px"
          borderRightColor={borderColor}
          position="relative"
        >
          {/* Gradient accent bar */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bgGradient={gradientAccent}
            zIndex={2}
          />

          {/* Enhanced close button */}
          <DrawerCloseButton
            zIndex={3}
            borderRadius="full"
            bg={menuBg}
            color={menuColor}
            _hover={{
              bg: menuHoverBg,
              transform: "rotate(90deg) scale(1.1)",
            }}
            _active={{
              transform: "rotate(90deg) scale(0.95)",
            }}
            _focus={{ boxShadow: "none" }}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            boxShadow={buttonShadow}
            top="12px"
            right="12px"
          />

          <DrawerBody maxW="285px" px="0rem" pb="0" pt="20px">
            <Scrollbars
              autoHide
              renderTrackVertical={renderTrack}
              renderThumbVertical={renderThumb}
              renderView={renderView}
            >
              <Content routes={routes} user={user} onNavigate={onClose} />
            </Scrollbars>
          </DrawerBody>

          {/* Decorative gradient orbs */}
          <Box
            position="absolute"
            top="25%"
            left="-40px"
            width="120px"
            height="120px"
            bgGradient={orb1}
            borderRadius="full"
            filter="blur(35px)"
            opacity={0.4}
            pointerEvents="none"
            zIndex={0}
          />
          <Box
            position="absolute"
            bottom="30%"
            right="-30px"
            width="100px"
            height="100px"
            bgGradient={orb2}
            borderRadius="full"
            filter="blur(30px)"
            opacity={0.4}
            pointerEvents="none"
            zIndex={0}
          />
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

SidebarResponsive.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  scrolled: PropTypes.bool,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  variant: PropTypes.string,
  logoText: PropTypes.string,
  message: PropTypes.string,
  brandText: PropTypes.string,
};

export default SidebarResponsive;